import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import './CreateOffer.css';

function EditOffer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  
  const [formData, setFormData] = useState({
    customerId: '',
    customerName: '',
    customerAddress: '',
    customerTaxNo: '',
    customerTaxOffice: '',
    contactPerson: '',
    validityDate: '',
    currency: 'EUR',
    introText: '',
    kdvRate: 20,
    hasMaintenance: false,
    maintenanceStartDate: '',
    items: []
  });

  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [discount, setDiscount] = useState(0);

  useEffect(() => {
    fetchProducts();
    fetchCustomers();
    fetchOffer();
  }, [id]);

  const fetchOffer = async () => {
    try {
      const response = await api.get(`/offers/${id}`);
      const offer = response.data.data;
      
      // Form verilerini doldur
      setFormData({
        customerId: '',
        customerName: offer.customer_name,
        customerAddress: offer.customer_address || '',
        customerTaxNo: offer.customer_tax_no || '',
        customerTaxOffice: offer.customer_tax_office || '',
        contactPerson: offer.contact_person || '',
        validityDate: offer.validity_date ? offer.validity_date.split('T')[0] : '',
        currency: offer.currency,
        introText: offer.intro_text || '',
        kdvRate: offer.kdv_rate,
        hasMaintenance: offer.has_maintenance,
        maintenanceStartDate: offer.maintenance_start_date ? offer.maintenance_start_date.split('T')[0] : '',
        items: offer.items.map(item => ({
          id: Date.now() + Math.random(),
          productId: item.product_id,
          productName: item.product_name,
          productCode: item.product_code,
          quantity: item.quantity,
          unitPrice: parseFloat(item.unit_price),
          discountPercent: parseFloat(item.discount_percent)
        }))
      });
    } catch (error) {
      console.error('Teklif yüklenemedi:', error);
      alert('Teklif bulunamadı');
      navigate('/offers');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products?active=true');
      setProducts(response.data.data);
    } catch (error) {
      console.error('Ürünler yüklenemedi:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers?active=true');
      setCustomers(response.data.data);
    } catch (error) {
      console.error('Müşteriler yüklenemedi:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleCustomerChange = (e) => {
    const customerId = e.target.value;
    
    if (customerId) {
      const customer = customers.find(c => c.id === parseInt(customerId));
      if (customer) {
        setFormData({
          ...formData,
          customerId,
          customerName: customer.name,
          customerAddress: customer.address || '',
          customerTaxNo: customer.tax_no || '',
          customerTaxOffice: customer.tax_office || '',
          contactPerson: customer.contact_person || ''
        });
      }
    }
  };

  const handleAddItem = () => {
    if (!selectedProduct) {
      alert('Lütfen bir ürün seçin');
      return;
    }
    const product = products.find(p => p.id === parseInt(selectedProduct));
    
    if (!product) {
      alert('Ürün bulunamadı');
      return;
    }

    const newItem = {
      id: Date.now(),
      productId: product.id,
      productName: product.name,
      productCode: product.code,
      quantity: parseInt(quantity) || 1,
      unitPrice: parseFloat(product.unit_price),
      discountPercent: parseFloat(discount) || 0
    };

    setFormData({
      ...formData,
      items: [...formData.items, newItem]
    });
    setSelectedProduct('');
    setQuantity(1);
    setDiscount(0);
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const calculateItemTotal = (item) => {
    const subtotal = item.unitPrice * item.quantity;
    const discountAmount = subtotal * (item.discountPercent / 100);
    return subtotal - discountAmount;
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
    const kdv = subtotal * (formData.kdvRate / 100);
    const total = subtotal + kdv;
    return { subtotal, kdv, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.items.length === 0) {
      alert('Lütfen en az bir ürün ekleyin');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/offers/${id}`, formData);
      alert('Teklif başarıyla güncellendi!');
      navigate(`/offers/${id}`);
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.message || 'Teklif güncellenemedi'));
    } finally {
      setLoading(false);
    }
  };

  const currencySymbols = { EUR: '€', USD: '$', TRY: '₺', GBP: '£' };
  const totals = calculateTotals();

  return (
    <div className="create-offer-page">
      <div className="page-header">
        <h1>✏️ Teklif Düzenle</h1>
        <button className="btn-back" onClick={() => navigate(`/offers/${id}`)}>
          ← Geri
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h2>📋 Teklif Bilgileri</h2>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Müşteri Seç (Firma Kodu) *</label>
              <select
                name="customerId"
                value={formData.customerId}
                onChange={handleCustomerChange}
              >
                <option value="">Müşteri Seçiniz</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.customer_code} - {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Para Birimi *</label>
              <select
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                required
              >
                <option value="EUR">EUR (€)</option>
                <option value="USD">USD ($)</option>
                <option value="TRY">TRY (₺)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Geçerlilik Tarihi</label>
              <input
                type="date"
                name="validityDate"
                value={formData.validityDate}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>KDV Oranı (%)</label>
              <input
                type="number"
                name="kdvRate"
                value={formData.kdvRate}
                onChange={handleChange}
                min="0"
                max="100"
              />
            </div>

            <div className="form-group full-width">
              <label>Ön Yazı</label>
              <textarea
                name="introText"
                value={formData.introText}
                onChange={handleChange}
                rows="4"
                placeholder="Sevgili müşterimiz, teklifimizi sunmaktan mutluluk duyarız..."
              />
            </div>
          </div>
        </div>

        {formData.customerId && (
          <div className="form-section">
            <h2>👤 Müşteri Bilgileri</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Müşteri Adı</label>
                <input
                  type="text"
                  value={formData.customerName}
                  readOnly
                  style={{background: '#f5f5f5', cursor: 'not-allowed'}}
                />
              </div>

              <div className="form-group">
                <label>İlgili Kişi</label>
                <input
                  type="text"
                  value={formData.contactPerson}
                  readOnly
                  style={{background: '#f5f5f5', cursor: 'not-allowed'}}
                />
              </div>

              <div className="form-group full-width">
                <label>Adres</label>
                <textarea
                  value={formData.customerAddress}
                  readOnly
                  rows="2"
                  style={{background: '#f5f5f5', cursor: 'not-allowed'}}
                />
              </div>

              <div className="form-group">
                <label>Vergi No</label>
                <input
                  type="text"
                  value={formData.customerTaxNo}
                  readOnly
                  style={{background: '#f5f5f5', cursor: 'not-allowed'}}
                />
              </div>

              <div className="form-group">
                <label>Vergi Dairesi</label>
                <input
                  type="text"
                  value={formData.customerTaxOffice}
                  readOnly
                  style={{background: '#f5f5f5', cursor: 'not-allowed'}}
                />
              </div>
            </div>
          </div>
        )}

        <div className="form-section">
          <h2>📦 Ürünler</h2>
          
          <div className="add-product-section">
            <div className="form-grid">
              <div className="form-group">
                <label>Ürün Seç</label>
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                >
                  <option value="">Seçiniz</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.code} - {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Miktar</label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  min="1"
                />
              </div>

              <div className="form-group">
                <label>İskonto (%)</label>
                <input
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  min="0"
                  max="100"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>&nbsp;</label>
                <button type="button" className="btn-add" onClick={handleAddItem}>
                  + Ekle
                </button>
              </div>
            </div>
          </div>

          {formData.items.length > 0 && (
            <div className="items-table">
              <table>
                <thead>
                  <tr>
                    <th>Ürün</th>
                    <th>Miktar</th>
                    <th>Birim Fiyat</th>
                    <th>İskonto</th>
                    <th>Toplam</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <strong>{item.productCode}</strong><br />
                        <small>{item.productName}</small>
                      </td>
                      <td>{item.quantity} AD</td>
                      <td>{item.unitPrice.toFixed(2)} {currencySymbols[formData.currency]}</td>
                      <td>%{item.discountPercent.toFixed(2)}</td>
                      <td><strong>{calculateItemTotal(item).toFixed(2)} {currencySymbols[formData.currency]}</strong></td>
                      <td>
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => removeItem(index)}
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {formData.items.length > 0 && (
          <div className="totals-section">
            <div className="totals">
              <div className="total-row">
                <span>Ara Toplam:</span>
                <strong>{totals.subtotal.toFixed(2)} {currencySymbols[formData.currency]}</strong>
              </div>
              <div className="total-row">
                <span>KDV (%{formData.kdvRate}):</span>
                <strong>{totals.kdv.toFixed(2)} {currencySymbols[formData.currency]}</strong>
              </div>
              <div className="total-row grand-total">
                <span>GENEL TOPLAM:</span>
                <strong>{totals.total.toFixed(2)} {currencySymbols[formData.currency]}</strong>
              </div>
            </div>
          </div>
        )}

        <div className="form-section">
          <h2>🔧 Bakım Anlaşması</h2>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="hasMaintenance"
                checked={formData.hasMaintenance}
                onChange={handleChange}
              />
              Bakım anlaşması ekle (1 yıl)
            </label>
          </div>

          {formData.hasMaintenance && (
            <div className="form-group">
              <label>Bakım Başlangıç Tarihi</label>
              <input
                type="date"
                name="maintenanceStartDate"
                value={formData.maintenanceStartDate}
                onChange={handleChange}
              />
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={() => navigate(`/offers/${id}`)}>
            İptal
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Güncelleniyor...' : '✓ Teklifi Güncelle'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditOffer;
